/**
 * Sana yordamchilari — tashqi kutubxonasiz (native `Date`). Bazada `timestamptz`
 * (UTC) saqlanadi, bu yerdagi funksiyalar Toshkent vaqt zonasida ko'rsatish/hisoblash uchun.
 *
 * ⚠️ Toshkent UTC+5 da, yozgi vaqtga (DST) o'tmaydi — shuning uchun doimiy +5 soat
 * siljish bilan hisoblash xavfsiz va to'g'ri (`docs/02-trd.md`: `TZ = Asia/Tashkent`).
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000
const TASHKENT_OFFSET_MS = 5 * 60 * 60 * 1000

/** Sanaga berilgan kunlar sonini qo'shib, yangi `Date` qaytaradi (kirish qiymati o'zgarmaydi). */
export function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * MS_PER_DAY)
}

/** Berilgan sanani Toshkent vaqt zonasidagi kun boshiga (00:00) keltiradi (UTC instant qaytadi). */
export function startOfDayTashkent(date: Date): Date {
  const shifted = new Date(date.getTime() + TASHKENT_OFFSET_MS)
  shifted.setUTCHours(0, 0, 0, 0)
  return new Date(shifted.getTime() - TASHKENT_OFFSET_MS)
}

/** Ikki sana Toshkent vaqt zonasida bir xil kalendar kunga to'g'ri kelishini tekshiradi. */
export function isSameDayTashkent(a: Date, b: Date): boolean {
  return startOfDayTashkent(a).getTime() === startOfDayTashkent(b).getTime()
}

/**
 * Joriy oyning Toshkentdagi birinchi kuni, kun boshida (UTC instant). Kun boshi
 * arifmetikasi qayta yozilmagan: `startOfDayTashkent()` natijasi ustiga oy/yil siljishi
 * qo'yiladi. Dashboard oylik statistikasi va oylik byudjet shu chegaradan foydalanadi.
 */
export function startOfMonthTashkent(date: Date): Date {
  const dayStart = startOfDayTashkent(date)
  const wallClock = new Date(dayStart.getTime() + TASHKENT_OFFSET_MS)
  const firstOfMonthWallClock = Date.UTC(wallClock.getUTCFullYear(), wallClock.getUTCMonth(), 1)
  return new Date(firstOfMonthWallClock - TASHKENT_OFFSET_MS)
}

/** `monthStart`dan `months` oy keyingi (yoki manfiy bo'lsa oldingi) oy boshi. */
export function shiftMonthsTashkent(monthStart: Date, months: number): Date {
  const wallClock = new Date(monthStart.getTime() + TASHKENT_OFFSET_MS)
  const shifted = Date.UTC(wallClock.getUTCFullYear(), wallClock.getUTCMonth() + months, 1)
  return new Date(shifted - TASHKENT_OFFSET_MS)
}

/** Berilgan sananing Toshkent yili va oyi (oy 1–12). Oylik byudjet kaliti uchun. */
export function tashkentYearMonth(date: Date): { year: number; month: number } {
  const wallClock = new Date(startOfMonthTashkent(date).getTime() + TASHKENT_OFFSET_MS)
  return { year: wallClock.getUTCFullYear(), month: wallClock.getUTCMonth() + 1 }
}

/**
 * Ikki sana orasidagi to'liq 24 soatlik davrlar soni (pastga yaxlitlangan, ishorali).
 *
 * DIQQAT: bu KALENDAR kunlar emas. 23:30 dan ertangi 01:30 gacha `0` qaytaradi.
 * Foydalanuvchiga ko'rsatiladigan "8 kundan keyin" / "3 kun kechikdi" kabi matnlar
 * uchun `calendarDaysBetweenTashkent()` ishlatilsin — aks holda `checkUrgency()`
 * bilan ziddiyat chiqadi (u kalendar kunlari bo'yicha ishlaydi).
 */
export function daysBetweenExact(a: Date, b: Date): number {
  return Math.floor((b.getTime() - a.getTime()) / MS_PER_DAY)
}

/**
 * Ikki sana orasidagi KALENDAR kunlar farqi (Toshkent vaqti bo'yicha).
 * `checkUrgency()` bilan bir xil mantiqqa asoslanadi, shuning uchun ular
 * hech qachon bir-biriga zid natija bermaydi.
 *
 * Kanban va bot xabarlaridagi barcha "necha kun" matnlari shu funksiyadan olinadi.
 */
export function calendarDaysBetweenTashkent(a: Date, b: Date): number {
  const diff = startOfDayTashkent(b).getTime() - startOfDayTashkent(a).getTime()
  return Math.round(diff / MS_PER_DAY)
}

/** Tekshiruv muddati (`nextCheckAt`) o'tib ketganmi — Toshkent kunlari bo'yicha solishtiradi. */
export function isOverdue(nextCheckAt: Date | null, now: Date): boolean {
  return checkUrgency(nextCheckAt, now) === 'overdue'
}

/**
 * Kanban kartochkasi uchun shoshilinchlik holatini aniqlaydi (`docs/04-ui-ux.md`
 * "Shoshilinchlik ranglari" jadvaliga mos):
 * - `overdue` — muddat o'tib ketgan (🔴)
 * - `today` — bugun (🟡)
 * - `upcoming` — kelajakda (⚪)
 * - `none` — tekshiruv muddati yo'q (masalan, yakunlangan yoki hali belgilanmagan)
 */
export function checkUrgency(
  nextCheckAt: Date | null,
  now: Date,
): 'overdue' | 'today' | 'upcoming' | 'none' {
  if (nextCheckAt === null) return 'none'

  const due = startOfDayTashkent(nextCheckAt).getTime()
  const today = startOfDayTashkent(now).getTime()

  if (due < today) return 'overdue'
  if (due === today) return 'today'
  return 'upcoming'
}
